import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Autocomplete, Typography, TextButton, getOptionLabel, getOptionSelected, useOverlayContext } from 'seagull';
import { Plus } from 'seagull/icons';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { makeFailure, makeSuccess, sortAlphabetically } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { genericConnector } from '~/utils/react-hook-form.utils';
import { useServiceTemplatesForBilling } from './hooks/useServiceTemplatesForBilling';
export var ServiceTemplateOptionType = /*#__PURE__*/ function(ServiceTemplateOptionType) {
    ServiceTemplateOptionType["Service"] = "service";
    ServiceTemplateOptionType["NewService"] = "new-service";
    ServiceTemplateOptionType["EmptySearch"] = "empty-search";
    ServiceTemplateOptionType["IncompleteSearch"] = "incomplete-search";
    return ServiceTemplateOptionType;
}({});
export function getServiceTemplateOptions(serviceTemplates) {
    let excludeBundles = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    const options = (excludeBundles ? serviceTemplates.filter((st)=>!(st === null || st === void 0 ? void 0 : st.bundle)) : serviceTemplates).map((st)=>({
            value: st.id,
            label: st.name,
            data: st,
            type: "service"
        }));
    return sortAlphabetically(options, 'label', {
        ascending: true,
        ignoreCase: true
    });
}
export const ServiceTemplateInputTestIds = pathgen('ServiceTemplateInputTestIds');
export const ServiceTemplateInput = (props)=>{
    var _ref;
    const { testId, applyServiceChange, renderProps, relationship, menuWidth, excludeBundles = true } = props;
    const [currentServiceTemplateOption, setCurrentServiceTemplateOption] = useState(null);
    const serviceTemplateApi = useServiceTemplateApi();
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: texts } = useLocaleKeys('components.serviceTemplateInput');
    const { field } = renderProps;
    const { serviceTemplates, isLoading, error: serviceTemplatesError, refresh: refreshServiceTemplates } = useServiceTemplatesForBilling({
        refreshOnMount: true
    });
    const serviceTemplateOptions = useMemo(()=>{
        return getServiceTemplateOptions(serviceTemplates, excludeBundles);
    }, [
        serviceTemplates,
        excludeBundles
    ]);
    const value = currentServiceTemplateOption;
    const valueLabel = (_ref = value === null || value === void 0 ? void 0 : value.label) !== null && _ref !== void 0 ? _ref : '';
    const applyServiceOptionChange = (newServiceOption)=>{
        setCurrentServiceTemplateOption(newServiceOption);
        const service = newServiceOption === null || newServiceOption === void 0 ? void 0 : newServiceOption.data;
        field.onChange(service === null || service === void 0 ? void 0 : service.id);
        applyServiceChange === null || applyServiceChange === void 0 ? void 0 : applyServiceChange(service);
    };
    const openCreateServiceTemplateModal = async ()=>{
        let newService;
        const onSave = async (service)=>{
            try {
                const { id } = await serviceTemplateApi.create(serviceTemplateModel.fromService(service));
                const { data: newServiceTemplates } = await refreshServiceTemplates();
                newService = newServiceTemplates === null || newServiceTemplates === void 0 ? void 0 : newServiceTemplates.find((s)=>s.id === id);
                overlayContext.showSuccessToast(commonTexts.toasts.serviceCreatedSuccessfully(), 'service-created-success');
                return makeSuccess({
                    id
                });
            } catch (error) {
                overlayContext.showErrorToast(commonTexts.toasts.failedToCreateService(), 'service-creation-failed');
                return makeFailure(error);
            }
        };
        await overlayContext.showModal({
            biName: 'service_builder_modal',
            transparentBackdrop: true,
            content: (p)=>/*#__PURE__*/ _jsx(ServiceBuilder, _object_spread_props(_object_spread({}, p), {
                    serviceNameBlacklist: serviceTemplates.map((s)=>s.name),
                    agreementData: AgreementDataModel.fromRelationship(relationship),
                    serviceOrigin: 'template',
                    onSave: (s)=>onSave(s)
                }))
        });
        return {
            newService
        };
    };
    if (serviceTemplatesError) throw serviceTemplatesError;
    return /*#__PURE__*/ _jsx(Autocomplete, {
        bi: {
            name: 'service_template'
        },
        selectOnFocus: true,
        size: "small",
        menuWidth: menuWidth,
        forcePopupIcon: true,
        testId: testId !== null && testId !== void 0 ? testId : ServiceTemplateInputTestIds.ServiceTemplateInput(),
        textFieldProps: {
            inputRef: field.ref,
            error: genericConnector(renderProps).showErrors
        },
        loading: isLoading,
        options: serviceTemplateOptions,
        renderOption: (option)=>{
            if ((option === null || option === void 0 ? void 0 : option.type) === "new-service") {
                return /*#__PURE__*/ _jsx(TextButton, {
                    testId: ServiceTemplateInputTestIds.NewServiceOption(),
                    underline: "none",
                    prefixIcon: /*#__PURE__*/ _jsx(Plus, {}),
                    children: texts.newService()
                });
            }
            return /*#__PURE__*/ _jsx(Typography, {
                as: "p",
                ellipsis: true,
                children: option.label
            });
        },
        filterOptions: (options)=>{
            const NO_MATCHES_OPTION = {
                value: 'empty-search',
                label: texts.noMatchesFound(),
                type: "empty-search"
            };
            const NEW_SERVICE_OPTION = {
                value: 'new-service',
                label: texts.newService(),
                type: "new-service"
            };
            const filteredOptions = options.filter((option)=>option.label.toLowerCase().includes(valueLabel.toLowerCase()));
            return [
                NEW_SERVICE_OPTION,
                ...filteredOptions.length === 0 ? [
                    NO_MATCHES_OPTION
                ] : [],
                ...filteredOptions
            ];
        },
        freeSolo: true,
        value: value,
        inputValue: valueLabel,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onBlur: ()=>{
            if ((currentServiceTemplateOption === null || currentServiceTemplateOption === void 0 ? void 0 : currentServiceTemplateOption.type) !== "service") {
                applyServiceOptionChange(null);
            }
        },
        onInputChange: (_, value, reason)=>{
            if (reason === 'clear') {
                applyServiceOptionChange(null);
                return;
            }
            if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
            const option = serviceTemplateOptions.find((s)=>s.label === value);
            const fallbackServiceOptionValue = {
                value: '',
                label: value,
                type: "incomplete-search"
            };
            applyServiceOptionChange(option !== null && option !== void 0 ? option : fallbackServiceOptionValue);
        },
        getOptionDisabled: (option)=>option.type === "empty-search",
        onChange: async (event, value)=>{
            const newValue = value ? value : null;
            if ((newValue === null || newValue === void 0 ? void 0 : newValue.type) === "new-service") {
                const { newService } = await openCreateServiceTemplateModal();
                const newServiceOption = newService ? {
                    value: newService.id,
                    label: newService.name,
                    data: newService,
                    type: "service"
                } : null;
                applyServiceOptionChange(newServiceOption);
            } else {
                applyServiceOptionChange(newValue);
            }
        }
    });
};
